import React, { useEffect, useContext, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Axios from "axios";
import DOMPurify from "dompurify";
import toast from "react-hot-toast";
import { buildUserState } from "../../utilities/tokenUtils";
import UserContext from "../../context/UserContext";

const schema = yup.object().shape({
    totpCode: yup
        .string()
        .required("El código MFA es requerido")
        .matches(/^[0-9]{6}$/, "Debe ser un código de 6 dígitos"),
});

export default function MFASetup() {
    const { userData, setUserData } = useContext(UserContext);
    const navigate = useNavigate();
    const [qrDataUrl, setQrDataUrl] = useState("");
    const [base32, setBase32] = useState("");
    const [mfaEnabled, setMfaEnabled] = useState(false);
    const [isEnabling, setIsEnabling] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    // Guard contra doble-invocación de useEffect en React 18 StrictMode
    const hasFetchedRef = useRef(false);

    // Flujo obligatorio: hay setupToken en sessionStorage pero aún no hay token final
    const isMandatorySetup = !!sessionStorage.getItem("setup-token") && !userData.token;
    const authToken = isMandatorySetup
        ? sessionStorage.getItem("setup-token")
        : userData.token;

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({ resolver: yupResolver(schema) });

    // useCallback garantiza referencia estable para incluirla en deps del useEffect
    const enableMFA = useCallback(async (tokenOverride) => {
        const token = tokenOverride || authToken;
        setIsEnabling(true);
        try {
            const res = await Axios.post(
                `${process.env.REACT_APP_BACK_DOMAIN}/users/enable-mfa`,
                {},
                { headers: { "x-auth-token": token } }
            );
            setQrDataUrl(res.data.qrDataUrl);
            setBase32(res.data.base32);
            setMfaEnabled(false);
            if (!isMandatorySetup) {
                toast.success(res.data.msg || "MFA habilitado. Escanea el QR.");
            }
        } catch (err) {
            toast.error(err.response?.data?.msg || "Error al habilitar MFA");
        } finally {
            setIsEnabling(false);
        }
    }, [authToken, isMandatorySetup]);

    useEffect(() => {
        if (!authToken) {
            navigate("/admin/login");
            return;
        }
        // Guard: previene la doble-invocación de React 18 StrictMode
        if (hasFetchedRef.current) return;
        hasFetchedRef.current = true;

        (async () => {
            try {
                const res = await Axios.get(
                    `${process.env.REACT_APP_BACK_DOMAIN}/users/mfa-status`,
                    { headers: { "x-auth-token": authToken } }
                );
                setMfaEnabled(res.data.enabled);
                // En flujo obligatorio siempre regenerar QR (cubre también el edge case
                // donde mfaEnabled=true pero el usuario debe reconfigurar)
                if (isMandatorySetup) {
                    await enableMFA(authToken);
                }
            } catch (err) {
                // En flujo obligatorio cualquier fallo de red/auth deja al usuario
                // sin forma de continuar (botón Regresar oculto), así que siempre
                // limpiar el setup-token y enviar al login.
                sessionStorage.removeItem("setup-token");
                const status = err.response?.status;
                if (status === 401 || status === 403) {
                    toast.error("Sesión expirada. Por favor inicie sesión nuevamente.");
                } else {
                    toast.error("No se pudo iniciar la configuración de MFA. Intente de nuevo.");
                }
                navigate("/admin/login");
            }
        })();
    }, [authToken, isMandatorySetup, enableMFA, navigate]);

    const onVerify = async (data) => {
        setIsVerifying(true);
        try {
            const sanitizedCode = DOMPurify.sanitize(data.totpCode);
            const res = await Axios.post(
                `${process.env.REACT_APP_BACK_DOMAIN}/users/verify-setup-mfa`,
                { tokenMFA: sanitizedCode },
                { headers: { "x-auth-token": authToken } }
            );

            if (isMandatorySetup && res.data.token) {
                // Flujo obligatorio: guardar token final y redirigir a inicio
                localStorage.setItem("auth-token", res.data.token);
                sessionStorage.removeItem("setup-token");
                setUserData(buildUserState(res.data.token));
                toast.success("MFA configurado. Bienvenido.");
                navigate("/");
            } else {
                toast.success(res.data.msg || "MFA configurado correctamente");
                setMfaEnabled(true);
                setQrDataUrl("");
            }
        } catch (err) {
            if (err.response?.status === 403) {
                toast.error("Código inválido o expirado. Vuelva a iniciar sesión.");
                sessionStorage.removeItem("setup-token");
                navigate("/admin/login");
            } else {
                toast.error(err.response?.data?.msg || "Error al verificar MFA");
            }
        } finally {
            setIsVerifying(false);
        }
    };

    return (
        <div className="page">
            <div className="container">
                {!isMandatorySetup && (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <button className="Button" onClick={() => navigate("/admin/profile")}>
                            Regresar
                        </button>
                    </div>
                )}

                {isMandatorySetup && (
                    <div className="info-block" style={{ borderLeft: "4px solid #e00000", paddingLeft: "12px" }}>
                        <h3>Configuración de MFA obligatoria</h3>
                        <p>El acceso a la aplicación requiere autenticación en dos pasos (MFA). Por favor, escanea el código QR con tu app autenticadora y completa la configuración para continuar.</p>
                    </div>
                )}

                <div className="info-block">
                    <h3>Información de configuración MFA</h3>
                    <ol>
                        <li>
                            <strong>Habilita MFA</strong> haciendo clic en el botón. Generarás un código QR y una clave secreta.
                        </li>
                        <li>
                            <strong>Escanea el código QR</strong> con tu app de autenticación (Google Authenticator, Authy, etc.) o usa la clave secreta manual.
                        </li>
                        <li>
                            <strong>Introduce el código de 6 dígitos</strong> de tu app para completar la activación.
                        </li>
                    </ol>
                    <div className="info-tip">
                        <h4>Recomendaciones de seguridad</h4>
                        <ul>
                            <li>La autenticación en dos pasos incrementa la seguridad.</li>
                            <li>No compartas tu clave secreta ni el QR.</li>
                            <li>Si pierdes acceso a la app, contacta soporte.</li>
                            <li>Si generas un nuevo código y no lo validas, tu MFA anterior seguirá activo.</li>
                        </ul>
                    </div>
                    <p>
                        ¿Necesitas ayuda? Consulta la guía de tu autenticador o al administrador.
                    </p>
                </div>

                <div className="section-card">
                    <div style={{ textAlign: "center" }}>
                        {!isMandatorySetup && (
                            <button
                                className="Button Button_primary"
                                onClick={() => enableMFA()}
                                disabled={isEnabling}
                            >
                                {isEnabling ? "Generando..." : "Habilitar/Regenerar MFA"}
                            </button>
                        )}
                    </div>

                    {!mfaEnabled && qrDataUrl && (
                        <div style={{ textAlign: "center" }}>
                            <img
                                src={qrDataUrl}
                                alt="QR Code for MFA"
                            />
                            <p>
                                <strong>Código manual:</strong>{" "}
                                <code>
                                    {base32}
                                </code>
                            </p>
                            <form onSubmit={handleSubmit(onVerify)} className="mfa-form">
                                <div className="Form-Field">
                                    <label htmlFor="totpCode" className="Form-Label">
                                        Código de 6 dígitos:
                                    </label>
                                    <input
                                        id="totpCode"
                                        type="text"
                                        maxLength={6}
                                        inputMode="numeric"
                                        className="Form-Input"
                                        placeholder="123456"
                                        {...register("totpCode")}
                                    />
                                    {errors.totpCode && (
                                        <div>
                                            {errors.totpCode.message}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="submit"
                                    className="Button Button_primary"
                                    disabled={isVerifying}
                                >
                                    {isVerifying ? "Verificando..." : "Validar Token"}
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
